import { Car, Apple, Check } from 'lucide-react';

/**
 * The hero visual: a phone mockup showing the customer's tap-to-pay flow,
 * with a small dashboard card peeking out behind it. Hand-tuned for the
 * single-page marketing site; deliberately not generalized.
 */
export default function HeroMock() {
  return (
    <div className="relative w-full max-w-md mx-auto">
      {/* Backing dashboard card (peeks out behind the phone) */}
      <div className="absolute -right-6 top-8 hidden sm:block w-72 rotate-6 z-0">
        <div className="bg-white rounded-2xl shadow-lift border border-ink-300/40 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-500">
              Today
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-confetti-lime bg-confetti-lime-soft rounded-full px-2 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-confetti-lime" />
              Live
            </span>
          </div>
          <div className="font-display text-3xl font-bold text-ink-900">$847.50</div>
          <div className="text-xs text-ink-500 mt-1">23 sessions · 4 new monthlies</div>
          <div className="mt-4 flex items-end gap-1 h-10">
            {[40, 60, 35, 80, 55, 90, 70].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm"
                style={{
                  height: `${h}%`,
                  background:
                    i % 2 === 0
                      ? 'linear-gradient(180deg, #FF3D7F, #FF7A59)'
                      : 'linear-gradient(180deg, #3D7FFF, #9B6BFF)',
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Phone */}
      <div className="relative z-10 mx-auto w-[280px] sm:w-[320px]">
        <div className="bg-ink-900 rounded-[2.4rem] p-2.5 shadow-confetti">
          <div className="bg-cream-100 rounded-[1.9rem] overflow-hidden">
            {/* Status bar */}
            <div className="flex items-center justify-between px-6 pt-3 pb-1 text-[10px] font-semibold text-ink-700">
              <span>9:41</span>
              <div className="w-20 h-4 bg-ink-900 rounded-full" />
              <span>100%</span>
            </div>

            {/* App content */}
            <div className="px-5 pt-4 pb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-confetti-pink to-confetti-coral" />
                <div className="font-display font-bold text-sm text-ink-900">
                  Maple Lot · Spot 12
                </div>
              </div>

              <div className="bg-white rounded-2xl p-4 shadow-soft">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 mb-2">
                  Plate
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <Car className="w-4 h-4 text-ink-700" />
                  <span className="font-mono font-bold text-ink-900">ABC 123</span>
                </div>

                <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 mb-2">
                  Time
                </div>
                <div className="grid grid-cols-3 gap-1.5 mb-4">
                  {['1h', '2h', '4h'].map((t, i) => (
                    <div
                      key={t}
                      className={`text-center text-xs font-semibold py-2 rounded-lg ${
                        i === 1
                          ? 'bg-confetti-pink text-white'
                          : 'bg-cream-200 text-ink-700'
                      }`}
                    >
                      {t}
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between border-t border-ink-300/40 pt-3 mb-3">
                  <span className="text-sm text-ink-500">Total</span>
                  <span className="font-display font-bold text-2xl text-ink-900">$9.00</span>
                </div>

                <button className="w-full bg-ink-900 text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2">
                  <Apple className="w-4 h-4 fill-current" />
                  Pay
                </button>
              </div>

              <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-ink-500">
                <Check className="w-3 h-3 text-confetti-lime" />
                Secure · Stripe · Apple Pay
              </div>
            </div>
          </div>
        </div>

        {/* Floating "Payment received" pill */}
        <div className="absolute -left-6 -top-3 sm:-left-10 bg-white rounded-full shadow-lift border border-ink-300/40 pl-2 pr-4 py-1.5 flex items-center gap-2 animate-float-mid">
          <span className="w-6 h-6 rounded-full bg-confetti-lime flex items-center justify-center">
            <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
          </span>
          <span className="text-xs font-semibold text-ink-900">+$9.00 just landed</span>
        </div>

        {/* Floating monthly sub pill */}
        <div className="absolute -right-3 sm:-right-10 -bottom-4 bg-white rounded-2xl shadow-lift border border-ink-300/40 px-3.5 py-2.5 animate-float-slow">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-500">
            Monthly sub
          </div>
          <div className="font-display font-bold text-sm text-ink-900">
            +$150<span className="text-ink-500 font-medium">/mo</span> · auto-renew
          </div>
        </div>
      </div>
    </div>
  );
}
